/**
 * People for updates: who the mention picker offers, who applies (@Applier),
 * and who exists in the account (to drop mentions of people who left).
 *
 * The picker offers the BOARD's people — its subscribers and owners, the list
 * the People column picker uses (lib/useBoardColumns fetchBoardMembers) —
 * which needs only boards:read, so it works on an account that has not
 * approved users:read. Teams join when teams:read is approved; without it the
 * picker lists people only. Loaded once, lazily, the first time a mention
 * picker opens.
 *
 * At apply time, fetchAccountPeople reads the ACCOUNT's users and teams for
 * serializeUpdate's `known`; when that is refused, every mention is sent as
 * written and monday judges (so a person not on the target board is never
 * dropped for it).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { describeApiErrors, fetchBoardMembers } from './useBoardColumns';

export const USERS_QUERY = 'query { users (limit: 200, kind: non_guests) { id name } }';
export const TEAMS_QUERY = 'query { teams { id name } }';

async function ask(api, query) {
  let res;
  try {
    res = await api.api(query);
  } catch (e) {
    return { error: (e && e.message) || 'request failed' };
  }
  const error = describeApiErrors(res);
  if (error) return { error };
  return { data: res && res.data };
}

/** @returns {{ users: Array, teams: Array, error: string|null }} */
export async function fetchAccountPeople(api) {
  const [u, t] = await Promise.all([ask(api, USERS_QUERY), ask(api, TEAMS_QUERY)]);
  const users = (u.data && u.data.users) || [];
  const teams = (t.data && t.data.teams) || [];
  const error = u.error || t.error || null;
  if (error) console.error('Could not load the account people for mentions:', error);
  return { users, teams, error };
}

/**
 * The mention picker's list: the board's people, and the account's teams when
 * they can be read. `error` only when no people could be read.
 * @returns {{ users: Array, teams: Array, error: string|null }}
 */
export async function fetchMentionPeople(api, { itemId, boardId } = {}) {
  const noBoard = { members: [], error: 'No board to read people from' };
  const [members, t] = await Promise.all([
    itemId || boardId ? fetchBoardMembers(api, itemId, boardId).catch((e) => ({ members: [], error: (e && e.message) || 'request failed' })) : noBoard,
    ask(api, TEAMS_QUERY),
  ]);
  const users = members.members || [];
  // teams are optional: without teams:read the picker lists people only
  const teams = (t.data && t.data.teams) || [];
  const error = users.length ? null : members.error || null;
  if (error) console.error('Could not load the board people for mentions:', error);
  return { users, teams, error };
}

/**
 * Who applies, for @Applier: `me` (needs me:read), else the user monday's
 * context names, found among the board's people. null when neither works:
 * "@Applier" then stays as text.
 */
export async function readApplier(api, { userId, itemId, boardId } = {}) {
  try {
    const me = await api.api('query { me { id name } }');
    if (me && me.data && me.data.me && me.data.me.id) return { id: me.data.me.id, name: me.data.me.name };
  } catch (e) {
    // refused (no me:read) or failed: try the board below
  }
  if (!userId || !(itemId || boardId)) return null;
  const { members } = await fetchBoardMembers(api, itemId, boardId).catch(() => ({ members: [] }));
  const found = (members || []).find((u) => String(u.id) === String(userId));
  return found ? { id: found.id, name: found.name } : null;
}

/** Keys for serializeUpdate's `known`: "User:1", "Team:2". */
export function knownMentionKeys({ users = [], teams = [] }) {
  return new Set([...users.map((u) => `User:${u.id}`), ...teams.map((t) => `Team:${t.id}`)]);
}

/**
 * useAccountPeople(api, { itemId, boardId }) → { status, users, teams, error, load }
 * The mention picker's people (fetchMentionPeople), for the board the editor is on.
 * status: 'idle' | 'loading' | 'loaded'. `load()` is a no-op while a fetch is in
 * flight or once people are in hand; a fetch that ended empty is retried.
 */
export function useAccountPeople(api, { itemId, boardId } = {}) {
  const [state, setState] = useState({ status: 'idle', users: [], teams: [], error: null });
  const generation = useRef(0);
  const inFlight = useRef(false);

  useEffect(() => {
    generation.current += 1;
    inFlight.current = false;
    setState({ status: 'idle', users: [], teams: [], error: null });
    return () => {
      generation.current += 1;
    };
  }, [api, itemId, boardId]);

  const loaded = state.status === 'loaded' && (state.users.length > 0 || state.teams.length > 0);
  const load = useCallback(() => {
    if (inFlight.current || loaded) return;
    const gen = generation.current;
    inFlight.current = true;
    setState((prev) => ({ ...prev, status: 'loading' }));
    fetchMentionPeople(api, { itemId, boardId })
      .catch((e) => ({ users: [], teams: [], error: (e && e.message) || 'request failed' }))
      .then(({ users, teams, error }) => {
        if (gen !== generation.current) return;
        inFlight.current = false;
        setState({ status: 'loaded', users, teams, error });
      });
  }, [api, loaded, itemId, boardId]);

  return { ...state, load };
}
